<template>
  <div class="fieldset-birthday-parts">
    <label for="fieldset-email">
      <span class="label-text">{{ $t('formParts.label.birthDay') }}</span>
      <span class="required" v-if="required">{{ $t('formParts.required') }}</span>
    </label>

    <div class="fieldset-birthday-parts__input__content">
      <template v-if="I18n.locale === 'ja'">
        <!-- 言語設定日本語の場合、年/月/日の順番で表示 -->
        <div class="fieldset-birthday-parts__input__content-year" @click.stop="() => {}">
          <!-- 年 -->
          <atom-dropdown
            :placeholder="$tc('formParts.placeholder.birthDay.year')"
            :menuList="yearOptions"
            :selectedOptions="state.year.id ? [state.year] : []"
            :isOpenMenu="openBirthdayMenuId === 'year'"
            :hasCheck="false"
            :maxHeight="'165px'"
            :menuPosition="2161"
            :isDisabled="isDisabled"
            @onClickCurrent="handleCurrentClicked('year')"
            @onClickOption="handleOptionClicked"
          />
        </div>
        <div class="fieldset-birthday-parts__input__content-div">/</div>
        <div class="fieldset-birthday-parts__input__content-month" @click.stop="() => {}">
          <!-- 月 -->
          <atom-dropdown
            :placeholder="$tc('formParts.placeholder.birthDay.month')"
            :menuList="monthOptions"
            :selectedOptions="state.month.id ? [state.month] : []"
            :isOpenMenu="openBirthdayMenuId === 'month'"
            :hasCheck="false"
            :maxHeight="'165px'"
            :isDisabled="isDisabled"
            @onClickCurrent="handleCurrentClicked('month')"
            @onClickOption="handleOptionClicked"
          />
        </div>
        <div class="fieldset-birthday-parts__input__content-div">/</div>
        <div class="fieldset-birthday-parts__input__content-date" @click.stop="() => {}">
          <!-- 日 -->
          <atom-dropdown
            :placeholder="$tc('formParts.placeholder.birthDay.date')"
            :menuList="dateOptions"
            :selectedOptions="state.date.id ? [state.date] : []"
            :isOpenMenu="openBirthdayMenuId === 'date'"
            :hasCheck="false"
            :maxHeight="'165px'"
            :isDisabled="isDisabled"
            @onClickCurrent="handleCurrentClicked('date')"
            @onClickOption="handleOptionClicked"
          />
        </div>
      </template>
      <template v-else>
        <!-- 言語設定英語の場合、月/日/年の順番で表示 -->
        <div class="fieldset-birthday-parts__input__content-month" @click.stop="() => {}">
          <!-- 月 -->
          <atom-dropdown
            :placeholder="$tc('formParts.placeholder.birthDay.month')"
            :menuList="monthOptions"
            :selectedOptions="state.month.id ? [state.month] : []"
            :isOpenMenu="openBirthdayMenuId === 'month'"
            :hasCheck="false"
            :maxHeight="'165px'"
            :isDisabled="isDisabled"
            @onClickCurrent="handleCurrentClicked('month')"
            @onClickOption="handleOptionClicked"
          />
        </div>
        <div class="fieldset-birthday-parts__input__content-div">/</div>
        <div class="fieldset-birthday-parts__input__content-date" @click.stop="() => {}">
          <!-- 日 -->
          <atom-dropdown
            :placeholder="$tc('formParts.placeholder.birthDay.date')"
            :menuList="dateOptions"
            :selectedOptions="state.date.id ? [state.date] : []"
            :isOpenMenu="openBirthdayMenuId === 'date'"
            :hasCheck="false"
            :maxHeight="'165px'"
            :isDisabled="isDisabled"
            @onClickCurrent="handleCurrentClicked('date')"
            @onClickOption="handleOptionClicked"
          />
        </div>
        <div class="fieldset-birthday-parts__input__content-div">/</div>
        <div class="fieldset-birthday-parts__input__content-year" @click.stop="() => {}">
          <!-- 年 -->
          <atom-dropdown
            :placeholder="$tc('formParts.placeholder.birthDay.year')"
            :menuList="yearOptions"
            :selectedOptions="state.year.id ? [state.year] : []"
            :isOpenMenu="openBirthdayMenuId === 'year'"
            :hasCheck="false"
            :maxHeight="'165px'"
            :menuPosition="2161"
            :isDisabled="isDisabled"
            @onClickCurrent="handleCurrentClicked('year')"
            @onClickOption="handleOptionClicked"
          />
        </div>
      </template>
    </div>

    <form-error-message-parts
      :showError="showError"
      :message="errorMessage !== '' ? errorMessage : $tc('formParts.errorMessage.birthDay')"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType, reactive, ref } from '@vue/composition-api'
import dayjs from 'dayjs'
import FormErrorMessageParts from '@/components/common/form/FormErrorMessageParts.vue'
import DeviceInfo from '@/util/DeviceInfo'
import AtomDropdown, { DropDownDataType } from '@/components/atoms/AtomDropdown.vue'
import I18n from '@/locales/I18n'
import useDisplayDependingOnLang from '@/components/hook/useDisplayDependingOnLang'

export type OpenBirthdayMenuIdType = 'year' | 'month' | 'date' | ''

/**
 * フィールドセット 誕生日入力 バーツ
 */
export default defineComponent({
  name: 'FieldsetBirthdayParts',
  components: {
    AtomDropdown,
    FormErrorMessageParts,
  },
  props: {
    /**
     * 入力データ
     */
    value: {
      type: String,
      default: '',
    },
    /**
     * 必須フラグ
     */
    required: {
      type: Boolean,
      default: false,
    },
    /**
     * エラーフラグ
     */
    showError: {
      type: Boolean,
      default: false,
    },
    /**
     * エラーメッセージ
     */
    errorMessage: {
      type: String,
      default: '',
    },
    openBirthdayMenuId: {
      type: String as PropType<OpenBirthdayMenuIdType>,
      default: '',
    },
  },
  setup(props, context) {
    const { getDisplayMonth } = useDisplayDependingOnLang()

    /**
     * 登録されている生年月日
     * 生年月日未登録の場合はデフォルト値を返す
     */
    const year = props.value ? String(dayjs(props.value).year()) : ''
    const month = props.value ? String(dayjs(props.value).month() + 1) : ''
    const date = props.value ? String(dayjs(props.value).date()) : ''

    /** 画面に表示する月 */
    const displayMonthName = (targetMonth: string) => {
      if (!targetMonth) {
        return ''
      }
      return I18n.locale === 'ja'
        ? targetMonth.padStart(2, '0')
        : // 「1、2などを渡すと」iOSの場合にMMM、MMMM形式のフォーマットで正しく変換されないため、適当な年と日を付与している
          getDisplayMonth(`1980-${targetMonth.padStart(2, '0')}-01`)
    }
    const state = reactive({
      year: { id: year, name: year } as DropDownDataType,
      month: { id: month, name: displayMonthName(month) } as DropDownDataType,
      date: { id: date, name: date ? date.padStart(2, '0') : '' } as DropDownDataType,
    })

    const yearOptions = []
    const monthOptions = []
    const dateOptions = []
    for (let i = 1920; i <= new Date().getFullYear() - 3; i += 1) {
      yearOptions.push({ id: `${i}`, name: `${i}` })
    }
    for (let i = 1; i <= 12; i += 1) {
      monthOptions.push({ id: `${i}`, name: displayMonthName(String(i)) })
    }
    for (let i = 1; i <= 31; i += 1) {
      dateOptions.push({ id: `${i}`, name: String(i).padStart(2, '0') })
    }

    /**
     * 誕生日入力フォームの変更が可能かどうか
     * ブラウザの場合にisDisabled: trueにする
     */
    const isDisabled = ref(!DeviceInfo.isCordova())

    const closeOptionMenu = () => {
      context.emit('onClickCloseBirthday')
    }

    const handleCurrentClicked = (menuId: string) => {
      context.emit('onClickBirthday', menuId)
    }

    const handleOptionClicked = (option: DropDownDataType) => {
      if (props.openBirthdayMenuId === 'year') {
        state.year = option
      }
      if (props.openBirthdayMenuId === 'month') {
        state.month = option
      }
      if (props.openBirthdayMenuId === 'date') {
        state.date = option
      }
      if (state.year.id && state.month.id && state.date.id) {
        // 言語設定英語の場合にmonth.nameがJan、Febなどになるため、01、02などに変換して登録するデータ値となるようにしている
        context.emit(
          'input',
          `${state.year.name}/${String(state.month.id).padStart(2, '0')}/${state.date.name}`,
        )
      }
      closeOptionMenu()
    }

    onMounted(async () => {
      /**
       * Chromeのブラウザだと誕生日入力フォームが認証情報と誤認されてしまう。
       * そのため、DOM生成時はdisabled状態とすることでChromeに認識させないようにし、0.5秒後に入力可能な状態にすることで対処している。
       */
      await new Promise((resolve) => {
        setTimeout(resolve, 500)
      })
      isDisabled.value = false
    })

    return {
      I18n,
      state,
      isDisabled,
      yearOptions,
      monthOptions,
      dateOptions,
      handleCurrentClicked,
      handleOptionClicked,
    }
  },
  methods: {
    /**
     * Enter Push
     */
    emitEnter() {
      /**
       * Enterボタン押下を通知する
       * @event enter
       * @type {Object}
       */
      this.$emit('enter')
    },
  },
})
</script>

<style lang="scss" scoped>
.fieldset-birthday-parts {
  @include fieldSet();

  &__input {
    opacity: 0;

    &:focus + .fieldset-birthday-parts__dummy-input {
      background: $white;
    }

    &__content {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;

      &-year {
        flex-shrink: 0;
        width: 29.59%; // (101.5 / 343) * 100%

        @media screen and (min-width: $responsiveTabletWidth) {
          width: 31.64%;
        }
      }

      &-month {
        flex-shrink: 0;
        width: 29.59%; // (101.5 / 343) * 100%

        @media screen and (min-width: $responsiveTabletWidth) {
          width: 31.64%;
        }
      }

      &-date {
        flex-shrink: 0;
        width: 29.59%; // (101.5 / 343) * 100%

        @media screen and (min-width: $responsiveTabletWidth) {
          width: 31.64%;
        }
      }
    }
  }

  &__select {
    color: $gray75;

    &.fieldset-birthday-parts__select--default {
      color: #808080;
    }
  }

  &__dummy-input {
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    width: 100%;
    padding: 1.077em 1.23em;
    text-align: left;
    appearance: none;
    outline: none;
    border-radius: 4px;
    background-color: #fff;
    border: 1px solid #c7c7c7;
    backdrop-filter: blur(0.85em);
    color: $gray75;
    pointer-events: none;

    @include fontFormDefault();

    &--placeholder-shown {
      background: rgba(0, 0, 0, 5%);
      color: rgba(0, 0, 0, 20%);
    }

    &--validation-error {
      color: $red-light;
      border-color: rgba(255, 46, 0, 50%);
      background: #fff;
    }
  }
}
</style>
